import { useState, useEffect, forwardRef, useCallback, useRef, memo, ForwardedRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { v4 as uuid } from "uuid";
import { isFunction, isObject } from "lodash";
import { VariableSizeList, areEqual } from "react-window";
import { BOLD_TEXT, COLOR_BORDER, COLOR_TEXT, SIZE_TEXT } from "@/comn/features/foundation";
import { useOptions, UseOptionsProps, useModal } from "@/comn/hooks";
import { utils, comnUtils, comnEnvs } from "@/comn/utils";
import { Icon } from "@/comn/components";
import { COMN_CODE } from "@/comn/constants";
import { useResourceContext } from "@/comn/features/context/ResourceContext";

type InputCodeProps = UseOptionsProps & {
    exact?: boolean;
    edit?: boolean;
    mode?: "edit" | "view" | null;
    popupSize?: "sm" | "md" | "lg";
    popupParams?: any;
    searchParams?: any;
    viewType?: "label" | "value" | "both";

    value?: any;
    name?: string;
    placeholder?: string;
    readOnly?: boolean;
    readOnlyInput?: boolean;
    disabled?: boolean;
    maxLength?: number;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    borderColor?: keyof typeof COLOR_BORDER;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;

    disableLabel?: boolean;
    disableSearch?: boolean;

    callback?: (arg?: any) => void;
    onBlur?: (arg?: any) => void;
    onFocus?: (arg?: any) => void;
    onChange?: (arg?: any) => void;

    onBeforeClickSearch?: (arg?: any) => boolean;

    async?: any;
};

export const InputCode = forwardRef((props: InputCodeProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
        edit = true,
        mode,
        popupParams,
        disableLabel,
        disableSearch,
        viewType = "both",
        exact = true,
        readOnlyInput,
        /** useOptions props */
        area,
        async = area && COMN_CODE[area]?.async,
        asyncParams,
        comnCd,
        excludes,
        includes,
        filter,
        options: propsOptions,
        /** */
        value,
        name,
        readOnly,
        disabled,
        maxLength,
        placeholder,
        color,
        editColor,
        borderColor,
        bold,
        editBold,
        fontSize,
        onBlur,
        onFocus,
        onChange,
        callback,
        onBeforeClickSearch,
    } = props;

    const _props = Object.fromEntries(
        Object.entries({
            name,
            readOnly,
            disabled,
            maxLength,
            placeholder,
            onFocus,
        }).filter(([, value]) => value !== undefined),
    );

    const $ = useRef<any>({ containerRef: null, inputRef: null });

    const { openModal, closeModal } = useModal();
    const { options, asyncCode, data, valueKey, status } = useOptions({
        comnCd,
        area,
        options: propsOptions,
        excludes,
        includes,
        filter,
        async,
        asyncParams,
    });

    const [isOpen, setIsOpen] = useState(false);
    const hidden = mode === "view" ? true : mode === "edit" ? false : !edit;
    const option = options.find((option) => option.value === value);
    const optionLabel = option?.label;

    /** stat */
    const _ref = useRef<any>({ isOpenAuto: false, value, options, option });
    _ref.current.options = options;
    _ref.current.option = option;
    _ref.current.value = value;

    useEffect(() => {
        if (async) asyncCode(value);
    }, [async, value]);

    const open = () => {
        setIsOpen(true);
    };

    const handleCallback = (arg?: any, option?: any) => {
        if (callback) {
            const _ =
                arg ||
                Object.assign(option, {
                    data: data.find((item: any) => item[valueKey] === option.value),
                });

            callback(_);
        }
    };

    const handleClick = () => {
        if (readOnlyInput || readOnly || disabled) return;
        open();
    };

    const handleBlur = (event: any) => {
        // autocomplete이 닫혀 있고 인풋 Blur시 컴포넌트가 가지고 있는 options 중 해당하는게 없으면 값 제거
        if (exact && onChange && _ref.current.isOpenAuto === false) {
            if (!_ref.current.options.some((item: any) => item.value === _ref.current.value)) {
                onChange("");
            }
        }

        if (onBlur) onBlur(event);
    };

    const handleChange = (event: any) => {
        const nextValue = event.target.value;
        if (typeof maxLength === "number" && maxLength < nextValue.length) return;
        if (onChange) onChange(formatCode(nextValue));
        open();
    };

    const handleClickSearch = () => {
        if (disabled === true) return;
        const params = { ...(utils.toValues(popupParams) || {}), comnCd };
        if (onBeforeClickSearch && onBeforeClickSearch({ popupParams: params }) === false) return;
        const id = uuid();
        const popupKey = ((Object.keys(comnEnvs.popup).includes(area || "") && area) ||
            "comnCd") as keyof typeof comnEnvs.popup;
        const url = comnEnvs.popup[popupKey];
        openModal({
            id,
            url,
            params,
            size: "sm",
            draggable: true,
            callback: (data: any) => {
                closeModal(id);
                handleCallback(data);
                if (onChange) onChange(data.code);
            },
        });
    };

    const autoComplete = options.filter((_) => {
        return (
            value &&
            (_.value?.toUpperCase().includes(value?.toUpperCase?.()) ||
                _.label?.toUpperCase().includes(value?.toUpperCase?.()))
        );
    });

    return (
        <>
            <div
                ref={(element) => {
                    if (element) {
                        $.current.containerRef = element;
                    }
                }}
                className="w-full"
                data-async={async}
                data-status={status}
            >
                {hidden && (
                    <div
                        title={viewCode(value, { options, viewType }, true)}
                        className={classNames("w-fit", comnUtils.getViewStyle(color, bold, fontSize))}
                    >
                        {viewCode(value, { options, viewType })}
                    </div>
                )}
                <div hidden={hidden}>
                    <div className="w-full flex relative">
                        <input
                            {..._props}
                            ref={(element) => {
                                if (element) {
                                    $.current.inputRef = element;
                                    if (isFunction(ref)) {
                                        ref(element);
                                    } else if (ref?.current) {
                                        ref.current = element;
                                    }
                                }
                            }}
                            readOnly={readOnly || readOnlyInput}
                            value={formatCode(value)}
                            onBlur={handleBlur}
                            onClick={handleClick}
                            onChange={handleChange}
                            autoComplete="off"
                            className={classNames(
                                "input flex-1",
                                comnUtils.getEditStyle(editColor, editBold),
                                (!disableSearch || !disableLabel) && "rounded-r-none",
                                borderColor ? COLOR_BORDER[borderColor] : "",
                            )}
                        />
                        {!disableSearch && (
                            <button
                                type="button"
                                className={classNames(
                                    "button border-l-0 rounded-l-none",
                                    !disableLabel && "rounded-none border-x-0",
                                )}
                                onClick={handleClickSearch}
                                disabled={readOnly === true || disabled === true}
                            >
                                <Icon icon="search" size="xs" />
                            </button>
                        )}
                        {!disableLabel && (
                            <input
                                readOnly={true}
                                disabled={disabled}
                                value={optionLabel || ""}
                                title={optionLabel}
                                className={classNames(
                                    "input rounded-l-none flex-[2]",
                                    comnUtils.getEditStyle(editColor, editBold),
                                )}
                            />
                        )}

                        {!disabled && !readOnly && isOpen && !!autoComplete.length && (
                            <Auto
                                $InputCode={$}
                                _ref={_ref}
                                option={option}
                                options={options}
                                value={value}
                                exact={exact}
                                setIsOpen={setIsOpen}
                                autoComplete={autoComplete}
                                onChange={onChange}
                                callback={handleCallback}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* {isOpen && <List area={area} value={value} async={async} asyncParams={asyncParams} />} */}
        </>
    );
});

// const List = memo(
//     (props: any) => {
//         const { area, value, async, asyncParams } = props;

//         const $ = useRef({ resourceKey: null });
//         const { getKey, getSingle, asyncSingle } = useResourceContext();
//         const [resource, setResource] = useState(() => {
//             const result = getSingle(area, value, asyncParams);
//             const data = result?.value || [];
//             const options = result?.options || [];
//             $.current.resourceKey = result?.key || null;
//             return { data, options };
//         });

//         useEffect(() => {
//             (async () => {
//                 if (getKey(area, value, asyncParams) !== $.current.resourceKey) {
//                     try {
//                         const result = await asyncSingle(area, value, asyncParams);
//                         setResource({ data: result.value, options: result.options });
//                     } catch (error) {
//                         // Error occurred
//                     }
//                 }
//             })();
//         }, [area, value, async, asyncParams]);

//         console.log(resource);

//         return <div></div>;
//     },
//     (oldProps, newProps) => {
//         return (
//             oldProps.area === newProps.area &&
//             oldProps.value === newProps.value &&
//             oldProps.async === newProps.async &&
//             JSON.stringify(oldProps.asyncParams) === JSON.stringify(newProps.asyncParams)
//         );
//     },
// );

const Auto = (props: any) => {
    const {
        $InputCode,

        _ref,
        exact,
        autoComplete,
        setIsOpen,
        onChange,
        callback,
    } = props;

    const [cursor, setCursor] = useState(null);
    const _context = useRef<any>({
        list: null,
        selected: null,
        autoComplete: [],
        itemsHeight: [],
    });

    useEffect(() => {
        _ref.current.isOpenAuto = true;
        return () => {
            _ref.current.isOpenAuto = false;
        };
    }, []);

    useEffect(() => {
        _context.current.list.scrollToItem(cursor);
    }, [cursor]);

    const handleClose = (event?: any) => {
        if (event) {
            if (event.target === $InputCode.current.inputRef) return;
        }

        if (exact) {
            if (!_ref.current.option) {
                onChange("");
            }
        }
        setIsOpen(false);
    };

    const handleClickItem = (option: any) => {
        if (onChange) onChange(option.value);
        callback(undefined, option);
        setIsOpen(false);
    };

    useEffect(() => {
        _context.current.autoComplete = autoComplete;
    }, [autoComplete]);

    useEffect(() => {
        const handleKeydown = (event: any) => {
            switch (event.keyCode) {
                case 9: // tab
                case 27: // esc
                    handleClose();
                    break;
                case 38: // up
                    setCursor((prev: any) => {
                        const val = prev === null ? 0 : prev - 1;
                        const next = val < 0 ? prev : val;
                        _context.current.selected = next;
                        return next;
                    });
                    break;
                case 40: // down
                    setCursor((prev: any) => {
                        const val = prev === null ? 0 : prev + 1;
                        const next = val > _context.current.autoComplete.length - 1 ? prev : val;
                        _context.current.selected = next;
                        return next;
                    });
                    break;
                case 13: // enter
                    if (typeof _context.current.selected !== "number") return;
                    const value = _context.current.autoComplete[_context.current.selected].value;
                    if (onChange) onChange(value);
                    callback(undefined, _context.current.autoComplete[_context.current.selected]);
                    setIsOpen(false);
                    break;
            }
        };

        window.addEventListener("keydown", handleKeydown);
        window.addEventListener("wheel", handleClose);
        window.addEventListener("scroll", handleClose);
        window.addEventListener("resize", handleClose);
        window.addEventListener("click", handleClose);

        return () => {
            window.removeEventListener("keydown", handleKeydown);
            window.removeEventListener("wheel", handleClose);
            window.removeEventListener("scroll", handleClose);
            window.removeEventListener("resize", handleClose);
            window.removeEventListener("click", handleClose);
        };
    }, []);

    const { width, top, height, left } = $InputCode.current.containerRef.getBoundingClientRect();

    return createPortal(
        <div
            onClick={(event) => event.stopPropagation()}
            className="fixed translate-y-1 w-full rounded bg-background/90 min-w-[12rem] border-x border-t shadow z-[9999]"
            style={{ width, left, top: top + height + 4 }}
        >
            <VariableSizeList
                innerElementType="ul"
                ref={(node) => {
                    if (!node) return;
                    _context.current.list = node;
                }}
                outerRef={(node) => {
                    if (!node) return;
                    node.onwheel = (event: any) => {
                        event.stopPropagation();
                    };
                }}
                itemCount={autoComplete.length}
                width="100%"
                height={300}
                itemSize={(index) => _context.current.itemsHeight[index] || 40}
                itemData={{ autoCompleteContext: _context, selected: cursor, autoComplete, handleClickItem }}
            >
                {Row}
            </VariableSizeList>
        </div>,
        document.body,
    );
};

const Row = memo((props: any) => {
    const { data, index, style } = props;
    const { autoCompleteContext, selected, autoComplete, handleClickItem } = data;

    const option = autoComplete[index];
    const { value, label } = option;
    const lastItem = index === autoComplete.length - 1;
    const ref = useRef<any>({ resizeObserver: null });

    const refCallback = useCallback((element: any) => {
        if (!element) return;
        ref.current.resizeObserver = new ResizeObserver((entries) => {
            requestAnimationFrame(() => {
                entries.forEach((resizeObserverEntry) => {
                    let height = resizeObserverEntry.contentRect.height + 12;
                    if (!lastItem) height + 1;
                    if (height === 0) return;

                    const corrected = height >= 40 ? height : 40;
                    if (autoCompleteContext.current.itemsHeight[index] !== corrected) {
                        autoCompleteContext.current.itemsHeight[index] = corrected;
                        autoCompleteContext.current.list.resetAfterIndex(index);
                    }
                });
            });
        });
        ref.current.resizeObserver.observe(element);
    }, []);

    return (
        <li style={style}>
            <span
                ref={refCallback}
                aria-selected={selected === index}
                onClick={() => {
                    handleClickItem(option);
                }}
                className={classNames(
                    "flex cursor-pointer items-center w-full min-h-[40px] p-1.5 hover:bg-lightgray aria-selected:bg-lightgray",
                    !lastItem && "border-b",
                )}
            >{`[${value}] ${label}`}</span>
        </li>
    );
}, areEqual);

export const viewCode = (v: any, o?: any, all?: boolean) => {
    if (!o?.options) return;
    const option = o.options?.find(({ value }: any) => value === v);
    if (!option) return;
    const vt = option.value || "";
    const lt = option.label || "";
    const vtWithBracket = option.value ? `[${option.value}] ` : "";

    let view = o?.viewType === "value" ? vt : o?.viewType === "label" ? lt : vtWithBracket + lt;
    if (all === true) {
        view = vtWithBracket + lt;
    }
    return view;
};

export const formatCode = (v: any, o?: any) => {
    if (!v) return "";
    let f = String(v);
    return f;
};

export const unformatCode = (v: any, o?: any) => {
    if (!v) return undefined;
    let f = String(v);
    return f;
};
